// DateTimeRangeRepeatEditor.jsx — weekly recurrence controls for datetimerange modal

import React, { useMemo } from 'react';

import { useTheme } from 'context/ThemeContext';
import {
  BYDAY_LABELS,
  BYDAY_ORDER,
  expandWeeklyOccurrences,
  formatRecurrenceHint,
  normalizeRecurrence,
  toMillis,
} from 'utilities/DateTimeRangeRecurrence';
import { getLocalTimeZone, normalizeDateValue, timestampFromDate } from 'utilities/DateTime';

import DatePicker from 'components/datepicker/DatePicker';
import SelectInput from 'components/selectinput/SelectInput';

import './DateTimeRangeRepeatEditor.css';

const END_MODE_OPTIONS = [
  { value: 'until', label: 'On date' },
  { value: 'count', label: 'After count' },
];

const COUNT_OPTIONS = Array.from({ length: 52 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value };
});

function buildDraftRecurrence({ enabled, byDay, endMode, untilDate, count, exDates }) {
  if (!enabled) return null;
  return {
    freq: 'WEEKLY',
    byDay: Array.isArray(byDay) ? byDay : [],
    interval: 1,
    until: endMode === 'until' && untilDate ? timestampFromDate(untilDate) : null,
    count: endMode === 'count' ? Number(count) || null : null,
    exDates: Array.isArray(exDates) ? exDates : [],
  };
}

/**
 * DateTimeRangeRepeatEditor
 *
 * @param {object} value - Full datetimerange value including optional recurrence.
 * @param {Function} onChange - Called with next full value (range fields preserved).
 */
const DateTimeRangeRepeatEditor = ({ value, onChange }) => {
  const { theme } = useTheme();

  const recurrenceState = useMemo(() => {
    const normalized = normalizeRecurrence(value?.recurrence);
    const recurrence = normalized.ok ? normalized.recurrence : null;
    const enabled = Boolean(recurrence);
    const byDay = recurrence?.byDay?.length ? recurrence.byDay : ['MO'];
    const endMode = Number.isFinite(recurrence?.until) || recurrence?.until
      ? 'until'
      : 'count';
    const untilDate = recurrence?.until
      ? normalizeDateValue(recurrence.until)
      : null;
    const count = recurrence?.count || 10;
    return {
      enabled,
      byDay,
      endMode: enabled ? endMode : 'until',
      untilDate,
      count,
      exDates: recurrence?.exDates || [],
      invalidError: normalized.ok ? null : normalized.error,
    };
  }, [value?.recurrence]);

  const summary = useMemo(() => {
    if (!recurrenceState.enabled) return '';
    const draft = buildDraftRecurrence(recurrenceState);
    const normalized = normalizeRecurrence(draft);
    if (!normalized.ok || !normalized.recurrence) {
      return recurrenceState.invalidError || 'Finish the repeat settings.';
    }

    const startMs = toMillis(value?.startDate);
    const endMs = toMillis(value?.endDate);
    if (!Number.isFinite(startMs)) {
      return formatRecurrenceHint(normalized.recurrence);
    }

    const expansion = expandWeeklyOccurrences({
      startMs,
      endMs: Number.isFinite(endMs) ? endMs : startMs + 60 * 60 * 1000,
      recurrence: normalized.recurrence,
      timeZone: getLocalTimeZone(),
    });

    const hint = formatRecurrenceHint(normalized.recurrence);
    if (!expansion.ok) return expansion.error || hint;
    return `${hint} (${expansion.occurrences.length} times)`;
  }, [recurrenceState, value?.endDate, value?.startDate]);

  const emit = (nextState) => {
    if (typeof onChange !== 'function') return;
    const nextRecurrence = buildDraftRecurrence(nextState);
    if (!nextRecurrence) {
      const { recurrence: _omit, ...rest } = value || {};
      onChange({ ...rest });
      return;
    }
    onChange({
      ...(value || {}),
      recurrence: nextRecurrence,
    });
  };

  const toggleDay = (token) => {
    const set = new Set(recurrenceState.byDay);
    if (set.has(token)) set.delete(token);
    else set.add(token);
    const byDay = BYDAY_ORDER.filter((day) => set.has(day));
    emit({
      ...recurrenceState,
      enabled: true,
      byDay: byDay.length ? byDay : [token],
    });
  };

  return (
    <div className="date-time-range-repeat-editor">
      <label className="date-time-range-repeat-editor__toggle">
        <input
          type="checkbox"
          checked={recurrenceState.enabled}
          onChange={(event) => {
            if (event.target.checked) {
              const defaultUntil = normalizeDateValue(value?.startDate)
                || new Date();
              const untilDate = new Date(defaultUntil);
              untilDate.setMonth(untilDate.getMonth() + 3);
              emit({
                enabled: true,
                byDay: recurrenceState.byDay,
                endMode: 'until',
                untilDate,
                count: 10,
              });
              return;
            }
            emit({ ...recurrenceState, enabled: false });
          }}
        />
        <span style={{ color: theme.foreground }}>Repeat weekly</span>
      </label>

      {recurrenceState.enabled ? (
        <>
          <div className="date-time-range-repeat-editor__days">
            {BYDAY_ORDER.map((token) => {
              const active = recurrenceState.byDay.includes(token);
              return (
                <button
                  key={token}
                  type="button"
                  className={[
                    'date-time-range-repeat-editor__day',
                    active ? 'date-time-range-repeat-editor__day--active' : '',
                  ].filter(Boolean).join(' ')}
                  style={{
                    backgroundColor: active ? theme.backgroundHover : theme.background,
                    color: theme.foreground,
                    borderColor: theme.border || theme.backgroundHover,
                  }}
                  onClick={() => toggleDay(token)}
                >
                  {BYDAY_LABELS[token]}
                </button>
              );
            })}
          </div>

          <div className="date-time-range-repeat-editor__ends">
            <span className="date-time-range-repeat-editor__ends-label" style={{ color: theme.foregroundMuted }}>
              Ends
            </span>
            <SelectInput
              value={recurrenceState.endMode}
              options={END_MODE_OPTIONS}
              onChange={(event) => {
                const nextMode = event?.target?.value || event;
                emit({
                  ...recurrenceState,
                  enabled: true,
                  endMode: nextMode,
                  untilDate: recurrenceState.untilDate
                    || normalizeDateValue(value?.startDate)
                    || new Date(),
                  count: recurrenceState.count || 10,
                });
              }}
            />
            {recurrenceState.endMode === 'until' ? (
              <DatePicker
                value={recurrenceState.untilDate}
                hybrid={false}
                onChange={(nextDate) => {
                  emit({
                    ...recurrenceState,
                    enabled: true,
                    endMode: 'until',
                    untilDate: nextDate,
                  });
                }}
              />
            ) : (
              <SelectInput
                value={String(recurrenceState.count || 10)}
                options={COUNT_OPTIONS}
                onChange={(event) => {
                  const nextCount = event?.target?.value || event;
                  emit({
                    ...recurrenceState,
                    enabled: true,
                    endMode: 'count',
                    count: Number(nextCount) || 10,
                  });
                }}
              />
            )}
          </div>

          {summary ? (
            <div
              className="date-time-range-repeat-editor__summary"
              style={{ color: theme.foregroundMuted }}
            >
              {summary}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

DateTimeRangeRepeatEditor.displayName = 'DateTimeRangeRepeatEditor';

export default DateTimeRangeRepeatEditor;
